import { useRef, useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  Button,
  Paper,
  Container,
  Alert,
  Snackbar,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Switch,
  FormControlLabel,
  Chip
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ImageIcon from "@mui/icons-material/Image";
import CropIcon from "@mui/icons-material/Crop";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FlipCameraIosIcon from "@mui/icons-material/FlipCameraIos";
import FlashlightOnIcon from "@mui/icons-material/FlashlightOn";
import FlashlightOffIcon from "@mui/icons-material/FlashlightOff";
import ModernHeader from "../components/modern/ModernHeader";
import { useNavigate } from "react-router-dom";
import makeAPIRequest from "../global/apiCall";
import { POST, UPLOAD_IMAGE, UPLOAD_MULTI_IMAGE } from "../global/apiKeys";
import SimpleAutoCrop from "../utils/simpleAutoCrop";

const Create = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const autoCropRef = useRef(null);

  // Simple states
  const [isCamera, setIsCamera] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [showCrop, setShowCrop] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [autoCropEnabled, setAutoCropEnabled] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [facingMode, setFacingMode] = useState('environment'); // 'user' for front, 'environment' for back
  const [detectionConfidence, setDetectionConfidence] = useState(0);
  const [detectionResult, setDetectionResult] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [hasFlash, setHasFlash] = useState(false);
  const [showManualCrop, setShowManualCrop] = useState(false);

  // Multi-side scanning states
  const [cardSide, setCardSide] = useState('front'); // 'front' or 'back'
  const [capturedImages, setCapturedImages] = useState({ front: null, back: null });
  const [showContinueDialog, setShowContinueDialog] = useState(false);

  // Start camera with minimal setup
  const startCamera = async () => {
    console.log('🎥 Starting camera...');
    setIsLoading(true);
    setError("");
    
    try {
      // Stop existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported on this browser');
      }

      console.log('🎥 Requesting camera permission...');
      
      // Camera request with facing mode and optional constraints
      const constraints = {
        video: { 
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Check if flash/torch is supported
      const track = stream.getVideoTracks()[0];
      const capabilities = track.getCapabilities();
      setHasFlash(capabilities && capabilities.torch === true);

      console.log('🎥 Camera stream obtained:', stream);
      streamRef.current = stream;
      
      // Set camera to active first so video element renders
      setIsCamera(true);
      setIsLoading(false);
      
      // Wait for video element to render, then set up stream
      setTimeout(() => {
        if (videoRef.current) {
          console.log('🎥 Setting video source...', videoRef.current);
          
          const video = videoRef.current;
          video.srcObject = stream;
          
          // Set video properties
          video.autoplay = true;
          video.playsInline = true;
          video.muted = true;

          // Handle video ready
          const onVideoReady = () => {
            console.log('🎥 Video is ready! Dimensions:', video.videoWidth, 'x', video.videoHeight);
            setSuccess("Camera ready! Position business card in view");
          };

          const onVideoError = (e) => {
            console.error('🎥 Video error:', e);
            setError("Failed to display camera feed. Please try again.");
          };

          // Add event listeners
          video.addEventListener('loadedmetadata', () => {
            console.log('🎥 Video metadata loaded, dimensions:', video.videoWidth, 'x', video.videoHeight);
          });
          
          video.addEventListener('canplay', onVideoReady);
          video.addEventListener('error', onVideoError);

          // Force play
          video.play()
            .then(() => {
              console.log('🎥 Video playback started successfully');
              setTimeout(onVideoReady, 100);
            })
            .catch((playError) => {
              console.error('🎥 Video play error:', playError);
              setTimeout(onVideoReady, 100);
            });
            
        } else {
          console.error('🎥 Video ref still null after timeout!');
          setError("Video element failed to render. Please refresh and try again.");
        }
      }, 100); // Short delay to let React render the video element
      
    } catch (err) {
      console.error("🎥 Camera error:", err);
      setIsLoading(false);
      setIsCamera(false);
      
      if (err.name === "NotAllowedError") {
        setError("Camera permission denied. Please refresh the page and allow camera access.");
      } else if (err.name === "NotFoundError") {
        setError("No camera found on this device.");
      } else if (err.name === "NotReadableError") {
        setError("Camera is being used by another application. Please close other apps and try again.");
      } else {
        setError(`Camera failed: ${err.message}`);
      }
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCamera(false);
    setCapturedImage(null);
  };

  // Switch camera between front and back
  const switchCamera = async () => {
    console.log('🔄 Switching camera from', facingMode);
    const newFacingMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newFacingMode);
    
    // Restart camera with new facing mode
    if (isCamera) {
      stopCamera();
      setTimeout(() => {
        startCamera();
      }, 500);
    }
    
    console.log('🔄 Camera switched to', newFacingMode === 'environment' ? 'back' : 'front');
  };

  // Toggle flashlight
  const toggleFlash = async () => {
    if (!streamRef.current || !hasFlash) return;
    
    try {
      const track = streamRef.current.getVideoTracks()[0];
      const newFlashState = !flashEnabled;
      
      await track.applyConstraints({
        advanced: [{ torch: newFlashState }]
      });
      
      setFlashEnabled(newFlashState);
      console.log('💡 Flash toggled:', newFlashState ? 'ON' : 'OFF');
    } catch (error) {
      console.error('💡 Flash toggle failed:', error);
      setError("Failed to toggle flashlight");
    }
  };

  // Calculate frame rectangle coordinates that match the UI guide
  const getFrameRectangle = (videoWidth, videoHeight) => {
    // Calculate frame size similar to UI responsive dimensions
    // UI uses: xs: '280px', sm: '320px', md: '360px'
    // We'll use a percentage-based approach for video dimensions
    
    const aspectRatio = 1.586; // Business card aspect ratio
    
    // Use roughly 70-80% of the smaller dimension to ensure the frame fits
    const maxFrameWidth = Math.min(videoWidth * 0.75, videoHeight * 0.75 * aspectRatio);
    const frameWidth = Math.floor(maxFrameWidth);
    const frameHeight = Math.floor(frameWidth / aspectRatio);
    
    // Center the frame
    const frameX = Math.floor((videoWidth - frameWidth) / 2);
    const frameY = Math.floor((videoHeight - frameHeight) / 2);
    
    console.log('📸 Frame rectangle calculated:', {
      video: { width: videoWidth, height: videoHeight },
      frame: { x: frameX, y: frameY, width: frameWidth, height: frameHeight }
    });
    
    return {
      x: frameX,
      y: frameY,
      width: frameWidth,
      height: frameHeight
    };
  };

  // Capture photo
  const capturePhoto = async () => {
    console.log('📸 Capture photo button clicked!');
    console.log('📸 Video ref exists:', !!videoRef.current);
    console.log('📸 Canvas ref exists:', !!canvasRef.current);
    
    if (!videoRef.current || !canvasRef.current) {
      console.error('📸 Missing video or canvas ref');
      setError("Camera not ready. Please try again.");
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    console.log('📸 Video dimensions:', video.videoWidth, 'x', video.videoHeight);
    console.log('📸 Video ready state:', video.readyState);
    
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      console.error('📸 Video has no dimensions');
      setError("Video not ready. Please wait a moment and try again.");
      return;
    }

    const ctx = canvas.getContext('2d');

    // Get the frame area dimensions (the business card guide rectangle)
    const frameRect = getFrameRectangle(video.videoWidth, video.videoHeight);
    
    // Set canvas size to frame size only
    canvas.width = frameRect.width;
    canvas.height = frameRect.height;
    console.log('📸 Canvas set to frame size:', canvas.width, 'x', canvas.height);

    // Draw only the frame area from the video
    ctx.drawImage(
      video,
      frameRect.x, frameRect.y, frameRect.width, frameRect.height, // source rectangle (frame area)
      0, 0, frameRect.width, frameRect.height // destination (entire canvas)
    );
    console.log('📸 Frame area drawn to canvas');

    // Convert to image data
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    console.log('📸 Frame image data created, length:', imageData.length);
    
    stopCamera();
    
    // Since we're already capturing the frame area, we have a pre-cropped business card image
    // Set confidence to high since we captured exactly what was in the frame
    setCapturedImage(imageData);
    setDetectionResult({
      dataUrl: imageData,
      bounds: frameRect,
      confidence: 0.9, // High confidence since user aligned the card in the frame
      originalSize: { width: video.videoWidth, height: video.videoHeight }
    });
    setDetectionConfidence(0.9);
    setSuccess("Business card captured from frame! Ready to process.");
    
    console.log('📸 Frame-captured image ready for processing');

    // Store the captured image for the current side
    setCapturedImages(prev => {
      const newImages = { ...prev, [cardSide]: imageData };
      console.log('📸 Updated captured images:', newImages);
      console.log('📸 Storing image for side:', cardSide, 'Image data length:', imageData?.length);

      // If this is the back side and we now have both sides, use multi-side API
      if (cardSide === 'back' && newImages.front && newImages.back) {
        console.log('📤 Both sides captured! Will use multi-side API.');
        setTimeout(() => {
          console.log('📸 Both sides ready, showing crop dialog...');
          setShowCrop(true);
        }, 200);
      } else if (cardSide === 'back') {
        // Back side only (shouldn't happen in normal flow)
        setTimeout(() => {
          console.log('📸 Back side only captured, showing crop dialog...');
          setShowCrop(true);
        }, 200);
      }

      return newImages;
    });

    // Show continue dialog to ask user's choice after front side capture
    if (cardSide === 'front') {
      console.log('📱 Front side captured, showing continue dialog');
      setShowContinueDialog(true);
    }
  };

  // Handle user choice after front side capture
  const handleContinueWithFrontOnly = () => {
    setShowContinueDialog(false);
    // Process with front side only
    setTimeout(() => {
      setShowCrop(true);
    }, 200);
  };

  const handleScanBackSide = () => {
    setShowContinueDialog(false);
    setCardSide('back');
    setCapturedImage(null); // Clear current capture to restart scanning
    setSuccess("Now scan the back side of your business card!");
    console.log('🔄 Switched to back side scanning mode');

    // Automatically start camera for back side scanning
    setTimeout(() => {
      startCamera();
      console.log('📷 Auto-starting camera for back side scanning');
    }, 100);
  };

  const resetAllCaptures = () => {
    setCapturedImage(null);
    setCapturedImages({ front: null, back: null });
    setCardSide('front');
    setShowContinueDialog(false);
    setShowCrop(false);
    console.log('🔄 Reset all captures, back to front side');
  };

  // Upload image with auto-crop option
  const uploadImage = async (useAutoCrop = true) => {
    console.log('📤 Upload button clicked!');
    console.log('📤 Legacy captured image exists:', !!capturedImage);
    console.log('📤 Auto-crop enabled:', autoCropEnabled && useAutoCrop);
    console.log('📤 Front image exists:', !!capturedImages.front);
    console.log('📤 Back image exists:', !!capturedImages.back);
    console.log('📤 Current card side:', cardSide);

    // Check if we have any image to process (multi-side takes priority)
    const hasMultiSideImages = capturedImages.front || capturedImages.back;
    const hasLegacyImage = capturedImage;

    if (!hasMultiSideImages && !hasLegacyImage) {
      console.error('📤 No captured image to upload');
      setError("No image to process. Please capture an image first.");
      return;
    }

    console.log('📤 Starting upload process...');
    setIsLoading(true);
    setIsProcessing(true);
    setShowCrop(false);
    
    try {
      let result;

      // Debug: Log current state
      console.log('📤 Upload decision - Front exists:', !!capturedImages.front);
      console.log('📤 Upload decision - Back exists:', !!capturedImages.back);
      console.log('📤 Upload decision - Current side:', cardSide);

      // Prioritize multi-side processing if we have captured images
      if (capturedImages.front && capturedImages.back) {
        console.log('📤 ✅ USING MULTI-SIDE API - Both sides detected!');
        setSuccess("Processing both sides of business card...");

        // Use multi-side API
        const formData = new FormData();

        // Convert front image to blob
        const frontResponse = await fetch(capturedImages.front);
        const frontBlob = await frontResponse.blob();
        formData.append('frontImage', frontBlob, `business-card-front-${Date.now()}.jpg`);

        // Convert back image to blob
        const backResponse = await fetch(capturedImages.back);
        const backBlob = await backResponse.blob();
        formData.append('backImage', backBlob, `business-card-back-${Date.now()}.jpg`);

        console.log('📤 Making multi-side API request to:', UPLOAD_MULTI_IMAGE);
        result = await makeAPIRequest(POST, UPLOAD_MULTI_IMAGE, formData);
        console.log('📤 Multi-side API response:', result);

      } else if (capturedImages.front || capturedImages.back) {
        console.log('📤 🔄 USING SINGLE-SIDE API - One side from multi-side capture');
        setSuccess("Processing single side of business card...");

        // Get the available image (front takes priority)
        const singleImageData = capturedImages.front || capturedImages.back;
        const singleResponse = await fetch(singleImageData);
        const singleBlob = await singleResponse.blob();

        const formData = new FormData();
        formData.append('image', singleBlob, `business-card-single-${Date.now()}.jpg`);
        console.log('📤 FormData created for single side, uploading...');

        console.log('📤 Making single-side API request to:', UPLOAD_IMAGE);
        result = await makeAPIRequest(POST, UPLOAD_IMAGE, formData);
        console.log('📤 Single-side API response:', result);

      } else {
        console.log('📤 📷 USING LEGACY IMAGE PROCESSING');
        setSuccess("Processing business card...");

        let finalBlob = null;
        let fileName = `business-card-${Date.now()}.jpg`;

        // Check if we already have a frame-captured image (high confidence)
        if (detectionConfidence > 0.8) {
          console.log('📤 Using frame-captured image (no additional cropping needed)');
          setSuccess("Processing frame-captured business card...");
          // Image is already perfectly cropped from the camera frame
        } else if (autoCropEnabled && useAutoCrop && autoCropRef.current) {
          console.log('📤 Attempting auto-crop for gallery image...');
          setSuccess("Auto-cropping business card...");

          // Enable debug mode if needed
          autoCropRef.current.setDebugMode(debugMode);

          const croppedResult = await autoCropRef.current.cropFromBase64(capturedImage);

          if (croppedResult && croppedResult.blob) {
            console.log('📤 Auto-crop successful! Confidence:', croppedResult.confidence);
            finalBlob = croppedResult.blob;
            fileName = `cropped-business-card-${Date.now()}.jpg`;

            const confidencePercent = Math.round(croppedResult.confidence * 100);
            setSuccess(`Business card processed (${confidencePercent}% confidence)!`);
          } else {
            console.log('📤 Auto-crop failed, using original image');
            setSuccess("Using original image...");
          }
        }

        // Fallback to original image if cropping failed or disabled
        if (!finalBlob) {
          console.log('📤 Converting original image to blob...');
          const response = await fetch(capturedImage);
          finalBlob = await response.blob();
        }

        console.log('📤 Final blob created:', finalBlob.size, 'bytes');

        // Use single-side API with legacy logic
        const formData = new FormData();
        formData.append('image', finalBlob, fileName);
        console.log('📤 FormData created for legacy image, uploading...');

        console.log('📤 Making legacy API request to:', UPLOAD_IMAGE);
        result = await makeAPIRequest(POST, UPLOAD_IMAGE, formData);
        console.log('📤 Legacy API response:', result);
      }

      // Prepare data with both sides if available
      // Extract the actual response data
      const responseData = result?.data || result;

      const cardData = {
        ...responseData?.combinedData, // For multi-side results
        ...responseData?.data, // For single-side results
        // Use URLs from API response instead of base64 data
        frontImageUrl: responseData?.frontImageUrl,
        backImageUrl: responseData?.backImageUrl,
        // Include cardImages from API response if available
        cardImages: responseData?.cardImages,
        hasBothSides: responseData?.hasBothSides || !!(capturedImages.front && capturedImages.back),
        scanType: responseData?.scanType || (capturedImages.front && capturedImages.back ? 'dual' : 'single'),
        // Include all the original response data
        ...responseData
      };

      // Save result and navigate
      localStorage.setItem('uploadedFileData', JSON.stringify(cardData));

      const successMessage = capturedImages.front && capturedImages.back
        ? 'Business cards (both sides) processed successfully!'
        : 'Business card processed successfully!';
      setSuccess(successMessage);

      // Trigger credit refresh event for PricingCard component
      window.dispatchEvent(new CustomEvent('refreshCredits', {
        detail: {
          action: 'scan_completed',
          remainingCredits: result.remainingCredits
        }
      }));

      setTimeout(() => {
        console.log('📤 Navigating to /info');
        navigate('/info');
      }, 1500);
      
    } catch (err) {
      console.error("📤 Upload error:", err);
      setError(`Failed to process image: ${err.message}`);
      setIsLoading(false);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle gallery upload
  const handleGalleryUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const imageData = e.target.result;
      
      // For gallery images, we want to use auto-crop detection
      if (autoCropEnabled && autoCropRef.current) {
        console.log('📤 Auto-cropping gallery image...');
        setIsProcessing(true);
        
        try {
          autoCropRef.current.setDebugMode(debugMode);
          const croppedResult = await autoCropRef.current.cropFromBase64(imageData);
          
          if (croppedResult && croppedResult.dataUrl) {
            console.log('📤 Gallery auto-crop successful! Confidence:', croppedResult.confidence);
            setCapturedImage(croppedResult.dataUrl);
            setDetectionResult(croppedResult);
            setDetectionConfidence(croppedResult.confidence);
            
            const confidencePercent = Math.round(croppedResult.confidence * 100);
            if (croppedResult.confidence > 0.6) {
              setSuccess(`Business card detected in gallery image (${confidencePercent}% confidence)!`);
            } else {
              setError(`Low detection confidence (${confidencePercent}%). Consider retaking or repositioning the card.`);
            }
          } else {
            console.log('📤 Gallery auto-crop failed, using original image');
            setCapturedImage(imageData);
            setDetectionResult(null);
            setDetectionConfidence(0);
            setError("Could not detect business card in gallery image. Using full image.");
          }
        } catch (error) {
          console.error('📤 Gallery auto-crop error:', error);
          setCapturedImage(imageData);
          setDetectionResult(null);
          setDetectionConfidence(0);
          setError("Auto-crop failed for gallery image.");
        } finally {
          setIsProcessing(false);
        }
      } else {
        // Auto-crop disabled, use original image
        setCapturedImage(imageData);
        setDetectionResult(null);
        setDetectionConfidence(0);
      }
      
      setShowCrop(true);
    };
    reader.readAsDataURL(file);
  };

  // Reset capture
  const resetCapture = () => {
    setCapturedImage(null);
    setShowCrop(false);
    setError("");
    setSuccess("");
  };

  // Initialize auto-crop utility
  useEffect(() => {
    if (!autoCropRef.current) {
      autoCropRef.current = new SimpleAutoCrop();
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Close alerts
  const closeAlert = () => {
    setError("");
    setSuccess("");
  };

  return (
    <>
      <ModernHeader
        title={`Scan Business Card`}
        subtitle={`${cardSide === 'front' ? '📄 Front Side' : '📄 Back Side'} - Capture or upload your business card`}
        onBackPress={() => navigate(-1)}
        background="primary"
        showLogo={true}
      />

      {/* Auto-crop toggle */}
      <Container maxWidth="lg" sx={{ pt: 1, pb: 0 }} className="padding-compact">
        <Paper sx={{ p: 1.5, borderRadius: '12px', mb: 1.5 }} className="padding-compact">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeIcon sx={{ color: 'primary.main' }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Smart Auto-Crop
              </Typography>
              {autoCropEnabled && (
                <Chip 
                  label="AI Powered" 
                  size="small" 
                  color="primary" 
                  sx={{ fontSize: '10px', height: '20px' }}
                />
              )}
              {detectionConfidence > 0 && (
                <Chip 
                  label={`${Math.round(detectionConfidence * 100)}% confidence`}
                  size="small" 
                  color={detectionConfidence > 0.7 ? "success" : detectionConfidence > 0.4 ? "warning" : "error"}
                  sx={{ fontSize: '10px', height: '20px', ml: 1 }}
                />
              )}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={autoCropEnabled}
                  onChange={(e) => setAutoCropEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label=""
              sx={{ m: 0 }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, ml: 4 }}>
            Automatically detect and crop business card from photos
          </Typography>
        </Paper>
      </Container>

      <Container maxWidth="lg" sx={{ p: 1.5, minHeight: 'calc(100vh - 120px)' }} className="padding-compact">
        
        {/* Main Camera/Image Area */}
        <Paper
          className="camera-container"
          sx={{
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            background: '#000',
            width: '100%',
            aspectRatio: { xs: '4/3', sm: '16/10', md: '16/9' },
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxHeight: { xs: '60vh', sm: '50vh', md: '45vh' }
          }}
        >
          {/* Camera View */}
          {isCamera && (
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />

              {/* Blur overlay areas - outside the scanning frame */}
              {/* Calculate frame dimensions:
                  xs: 240px wide, height = 240/1.586 = ~151px
                  sm: 280px wide, height = 280/1.586 = ~177px
                  md: 320px wide, height = 320/1.586 = ~202px */}

              {/* Top blur area */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: {
                  xs: 'calc(50% - 76px)',   // (151px/2 = 75.5px)
                  sm: 'calc(50% - 89px)',   // (177px/2 = 88.5px)
                  md: 'calc(50% - 101px)'   // (202px/2 = 101px)
                },
                background: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(10px)',
                zIndex: 2,
              }} />

              {/* Bottom blur area */}
              <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                height: {
                  xs: 'calc(50% - 76px)',
                  sm: 'calc(50% - 89px)',
                  md: 'calc(50% - 101px)'
                },
                background: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(10px)',
                zIndex: 2,
              }} />

              {/* Left blur area */}
              <Box sx={{
                position: 'absolute',
                top: {
                  xs: 'calc(50% - 76px)',
                  sm: 'calc(50% - 89px)',
                  md: 'calc(50% - 101px)'
                },
                left: 0,
                width: {
                  xs: 'calc(50% - 120px)',  // (240px/2 = 120px)
                  sm: 'calc(50% - 140px)',  // (280px/2 = 140px)
                  md: 'calc(50% - 160px)'   // (320px/2 = 160px)
                },
                height: {
                  xs: '151px',  // Frame height
                  sm: '177px',
                  md: '202px'
                },
                background: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(10px)',
                zIndex: 2,
              }} />

              {/* Right blur area */}
              <Box sx={{
                position: 'absolute',
                top: {
                  xs: 'calc(50% - 76px)',
                  sm: 'calc(50% - 89px)',
                  md: 'calc(50% - 101px)'
                },
                right: 0,
                width: {
                  xs: 'calc(50% - 120px)',
                  sm: 'calc(50% - 140px)',
                  md: 'calc(50% - 160px)'
                },
                height: {
                  xs: '151px',
                  sm: '177px',
                  md: '202px'
                },
                background: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(10px)',
                zIndex: 2,
              }} />

              {/* Business card guide with proper aspect ratio */}
              <Box
                className="card-guide"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: '240px', sm: '280px', md: '320px' },
                  aspectRatio: '1.586/1', // Standard business card aspect ratio
                  border: '3px solid rgba(255, 255, 255, 0.9)',
                  borderRadius: '12px',
                  backgroundColor: 'transparent',
                  zIndex: 3,
                  animation: 'cardGuide 2s ease-in-out infinite',
                  '@keyframes cardGuide': {
                    '0%': { 
                      boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.4)',
                      borderColor: 'rgba(255, 255, 255, 0.9)'
                    },
                    '50%': { 
                      boxShadow: '0 0 0 8px rgba(255, 255, 255, 0.1)',
                      borderColor: 'rgba(255, 255, 255, 1)'
                    },
                    '100%': { 
                      boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.4)',
                      borderColor: 'rgba(255, 255, 255, 0.9)'
                    }
                  },
                  '&::before': {
                    content: '"📄"',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '48px',
                    opacity: 0.3
                  },
                  '&::after': {
                    content: `"Position ${cardSide} side in frame"`,
                    position: 'absolute',
                    top: '-40px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontSize: { xs: '12px', sm: '14px' },
                    fontWeight: '600',
                    textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                    background: 'rgba(0,0,0,0.6)',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    whiteSpace: 'nowrap'
                  }
                }}
              >
                {/* Corner indicators */}
                <Box sx={{
                  position: 'absolute',
                  top: -2,
                  left: -2,
                  width: 16,
                  height: 16,
                  border: '2px solid #fff',
                  borderRight: 'none',
                  borderBottom: 'none',
                  borderRadius: '4px 0 0 0'
                }} />
                <Box sx={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  width: 16,
                  height: 16,
                  border: '2px solid #fff',
                  borderLeft: 'none',
                  borderBottom: 'none',
                  borderRadius: '0 4px 0 0'
                }} />
                <Box sx={{
                  position: 'absolute',
                  bottom: -2,
                  left: -2,
                  width: 16,
                  height: 16,
                  border: '2px solid #fff',
                  borderRight: 'none',
                  borderTop: 'none',
                  borderRadius: '0 0 0 4px'
                }} />
                <Box sx={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  width: 16,
                  height: 16,
                  border: '2px solid #fff',
                  borderLeft: 'none',
                  borderTop: 'none',
                  borderRadius: '0 0 4px 0'
                }} />
              </Box>
            </Box>
          )}

          {/* Loading State */}
          {isLoading && (
            <Box sx={{ textAlign: 'center', color: 'white', p: 4 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  border: '3px solid rgba(255,255,255,0.3)',
                  borderTop: '3px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  mx: 'auto',
                  mb: 2
                }}
              />
              <Typography sx={{ mb: 2 }}>Starting Camera...</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
                Please allow camera access when prompted
              </Typography>
              
              <Button
                onClick={() => {
                  setIsLoading(false);
                  document.getElementById('gallery-input')?.click();
                }}
                variant="outlined"
                size="small"
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Skip Camera - Use Gallery
              </Button>
            </Box>
          )}

          {/* Auto-crop Processing State */}
          {isProcessing && (
            <Box sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              zIndex: 10
            }}>
              <Box sx={{ textAlign: 'center', color: 'white' }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    border: '3px solid rgba(255,255,255,0.3)',
                    borderTop: '3px solid #ff9800',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    mx: 'auto',
                    mb: 2
                  }}
                />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Detecting Business Card...
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Automatically cropping to card boundaries
                </Typography>
              </Box>
            </Box>
          )}

          {/* Default State */}
          {!isCamera && !isLoading && !capturedImage && (
            <Box sx={{ textAlign: 'center', color: 'white', p: 4 }}>
              <CameraAltIcon sx={{ fontSize: 60, mb: 2, opacity: 0.7 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                Ready to Scan
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, opacity: 0.8 }}>
                Click the camera button below to start
              </Typography>
            </Box>
          )}

          {/* Captured Image */}
          {capturedImage && (
            <img
              src={capturedImage}
              alt="Captured business card"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
            />
          )}
        </Paper>

        {/* Controls */}
        <Paper sx={{ p: 2, borderRadius: '12px', textAlign: 'center' }} className="padding-compact">
          {/* Main Camera Button Row */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
            {/* Main Action Button - Larger and Prominent */}
            {isCamera ? (
              <IconButton
                onClick={capturePhoto}
                disabled={isLoading}
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: '#1976d2',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                  '&:hover': {
                    bgcolor: '#1565c0',
                    transform: 'scale(1.05)',
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.5)'
                  },
                  '&:disabled': { opacity: 0.5 },
                  transition: 'all 0.2s ease'
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 36 }} />
              </IconButton>
            ) : capturedImage ? (
              <IconButton
                onClick={() => startCamera()}
                disabled={isLoading}
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: '#9c27b0',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(156, 39, 176, 0.4)',
                  '&:hover': {
                    bgcolor: '#7b1fa2',
                    transform: 'scale(1.05)',
                    boxShadow: '0 6px 16px rgba(156, 39, 176, 0.5)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <CameraAltIcon sx={{ fontSize: 36 }} />
              </IconButton>
            ) : (
              <IconButton
                onClick={startCamera}
                disabled={isLoading}
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: '#1976d2',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                  '&:hover': {
                    bgcolor: '#1565c0',
                    transform: 'scale(1.05)',
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.5)'
                  },
                  '&:disabled': { opacity: 0.5 },
                  transition: 'all 0.2s ease'
                }}
              >
                <CameraAltIcon sx={{ fontSize: 36 }} />
              </IconButton>
            )}
          </Box>

          {/* Secondary Controls Row */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, alignItems: 'center' }}>

            {/* Gallery Upload */}
            <Button
              component="label"
              disabled={isLoading}
              variant="outlined"
              sx={{
                width: 48,
                height: 48,
                minWidth: 48,
                borderRadius: '12px',
                borderColor: '#1976d2',
                color: '#1976d2',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0.5,
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 'bold', lineHeight: 1 }}>
                GAL
              </Typography>
              <input
                id="gallery-input"
                type="file"
                accept="image/*"
                hidden
                onChange={handleGalleryUpload}
              />
            </Button>

            {/* Camera Switch Button - Smaller */}
            {isCamera && (
              <Button
                onClick={switchCamera}
                disabled={isLoading}
                variant="outlined"
                sx={{
                  width: 48,
                  height: 48,
                  minWidth: 48,
                  borderRadius: '12px',
                  borderColor: '#ff9800',
                  color: '#ff9800',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0.5,
                  '&:hover': {
                    borderColor: '#f57c00',
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    transform: 'translateY(-2px)'
                  },
                  '&:disabled': { opacity: 0.5 },
                  transition: 'all 0.2s ease'
                }}
              >
                <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 'bold', lineHeight: 1 }}>
                  FLIP
                </Typography>
              </Button>
            )}

            {/* Flashlight Button - Smaller */}
            {isCamera && hasFlash && (
              <Button
                onClick={toggleFlash}
                disabled={isLoading}
                variant="outlined"
                sx={{
                  width: 48,
                  height: 48,
                  minWidth: 48,
                  borderRadius: '12px',
                  borderColor: flashEnabled ? '#ffc107' : '#6c757d',
                  color: flashEnabled ? '#ffc107' : '#6c757d',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0.5,
                  '&:hover': {
                    borderColor: flashEnabled ? '#ffb300' : '#5a6268',
                    backgroundColor: flashEnabled ? 'rgba(255, 193, 7, 0.1)' : 'rgba(108, 117, 125, 0.1)',
                    transform: 'translateY(-2px)'
                  },
                  '&:disabled': { opacity: 0.5 },
                  transition: 'all 0.2s ease'
                }}
              >
                <Typography variant="caption" sx={{ fontSize: 8, fontWeight: 'bold', lineHeight: 1 }}>
                  {flashEnabled ? 'ON' : 'OFF'}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: 7, lineHeight: 1, mt: 0.25 }}>
                  Flash
                </Typography>
              </Button>
            )}

            {/* Process Button */}
            <Button
              onClick={() => setShowCrop(true)}
              disabled={!capturedImage || isLoading}
              variant="outlined"
              sx={{
                width: 48,
                height: 48,
                minWidth: 48,
                borderRadius: '12px',
                borderColor: capturedImage ? '#9c27b0' : '#999',
                color: capturedImage ? '#9c27b0' : '#999',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0.5,
                '&:hover': {
                  borderColor: capturedImage ? '#7b1fa2' : '#999',
                  backgroundColor: capturedImage ? 'rgba(156, 39, 176, 0.1)' : 'transparent',
                  transform: capturedImage ? 'translateY(-2px)' : 'none'
                },
                '&:disabled': { opacity: 0.3 },
                transition: 'all 0.2s ease'
              }}
            >
              <Typography variant="caption" sx={{ fontSize: 8, fontWeight: 'bold', lineHeight: 1 }}>
                CROP
              </Typography>
            </Button>
          </Box>

          {capturedImage && (
            <Button
              onClick={resetCapture}
              variant="text"
              size="small"
              sx={{ mt: 2, textTransform: 'none' }}
            >
              Reset and try again
            </Button>
          )}
          
          {/* Debug button - temporary */}
          <Button
            onClick={() => {
              console.log('🐛 DEBUG: Force opening dialog');
              console.log('🐛 Current capturedImage:', !!capturedImage);
              setShowCrop(true);
            }}
            variant="text"
            size="small"
            sx={{ mt: 1, color: 'orange' }}
          >
            DEBUG: Force Open Dialog
          </Button>
        </Paper>
      </Container>

      {/* Crop/Process Dialog */}
      <Dialog 
        open={showCrop} 
        onClose={() => setShowCrop(false)} 
        maxWidth="sm" 
        fullWidth
        onTransitionEntered={() => {
          console.log('🎭 Dialog opened - capturedImage exists:', !!capturedImage);
          console.log('🎭 Dialog opened - showCrop:', showCrop);
        }}
      >
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Process Business Card
          </Typography>

          {/* Show both images if available, otherwise show single image */}
          {capturedImages.front && capturedImages.back ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                📄 Both Sides Captured
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                {/* Front Side */}
                <Box sx={{ flex: '1 1 45%', minWidth: '150px', maxWidth: '200px' }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'success.main' }}>
                    📄 Front Side
                  </Typography>
                  <Box sx={{ border: '2px solid #4caf50', borderRadius: '8px', p: 1 }}>
                    <img
                      src={capturedImages.front}
                      alt="Front side preview"
                      style={{ width: '100%', height: 'auto', maxHeight: '150px', borderRadius: '4px', objectFit: 'contain' }}
                    />
                  </Box>
                </Box>

                {/* Back Side */}
                <Box sx={{ flex: '1 1 45%', minWidth: '150px', maxWidth: '200px' }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'warning.main' }}>
                    📄 Back Side
                  </Typography>
                  <Box sx={{ border: '2px solid #ff9800', borderRadius: '8px', p: 1 }}>
                    <img
                      src={capturedImages.back}
                      alt="Back side preview"
                      style={{ width: '100%', height: 'auto', maxHeight: '150px', borderRadius: '4px', objectFit: 'contain' }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (capturedImages.front || capturedImages.back) ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                📄 {capturedImages.front ? 'Front Side' : 'Back Side'} Captured
              </Typography>
              <Box sx={{ border: '1px solid #ddd', borderRadius: '8px', p: 1, maxWidth: '300px', margin: '0 auto' }}>
                <img
                  src={capturedImages.front || capturedImages.back}
                  alt="Card preview"
                  style={{ width: '100%', height: 'auto', maxHeight: '200px', borderRadius: '4px', objectFit: 'contain' }}
                />
              </Box>
            </Box>
          ) : capturedImage ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                📄 Legacy Image
              </Typography>
              <Box sx={{ border: '1px solid #ddd', borderRadius: '8px', p: 1, maxWidth: '300px', margin: '0 auto' }}>
                <img
                  src={capturedImage}
                  alt="Preview"
                  style={{ width: '100%', height: 'auto', maxHeight: '200px', borderRadius: '4px', objectFit: 'contain' }}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ mb: 3, p: 2, border: '1px dashed #ccc', borderRadius: '8px' }}>
              <Typography color="error">No image captured!</Typography>
            </Box>
          )}

          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            {capturedImages.front && capturedImages.back
              ? 'Ready to extract information from both sides of your business card?'
              : 'Ready to extract information from your business card?'
            }
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, justifyContent: 'center', gap: 2 }}>
          <Button onClick={() => setShowCrop(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => {
              console.log('📤 Extract Data button clicked - checking images...');
              console.log('📤 Front image exists:', !!capturedImages.front);
              console.log('📤 Back image exists:', !!capturedImages.back);
              console.log('📤 Legacy captured image exists:', !!capturedImage);
              console.log('📤 isLoading:', isLoading);

              const hasAnyImage = capturedImages.front || capturedImages.back || capturedImage;
              console.log('📤 Has any image to process:', hasAnyImage);

              if (hasAnyImage) {
                uploadImage(true); // Use auto-crop if enabled
              } else {
                console.error('📤 No captured image available');
                setError("No image to process. Please capture an image first.");
                setShowCrop(false);
              }
            }}
            variant="contained"
            disabled={isLoading || (!capturedImages.front && !capturedImages.back && !capturedImage)}
            startIcon={autoCropEnabled && isProcessing ? <AutoAwesomeIcon /> : null}
            sx={{
              minWidth: 140,
              bgcolor: ((!capturedImages.front && !capturedImages.back && !capturedImage) || isLoading) ? '#ccc' : '#1976d2',
              '&:hover': {
                bgcolor: ((!capturedImages.front && !capturedImages.back && !capturedImage) || isLoading) ? '#ccc' : '#1565c0'
              }
            }}
          >
            {isLoading ?
              (isProcessing && autoCropEnabled ? 'Auto-Cropping...' : 'Processing...') :
              (capturedImages.front && capturedImages.back) ? 'Extract Both Sides' :
              (capturedImages.front || capturedImages.back || capturedImage) ? (autoCropEnabled ? 'Smart Extract' : 'Extract Data') : 'No Image'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden canvas for image processing */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Alerts */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={closeAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={closeAlert} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={closeAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={closeAlert} sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Continue Dialog */}
      <Dialog
        open={showContinueDialog}
        onClose={() => setShowContinueDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 8, // Valid elevation value
          sx: {
            borderRadius: '20px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          pb: 1,
          background: 'linear-gradient(135deg, #00ff88, #00cc66)',
          color: 'white',
          fontWeight: 600,
          fontSize: '1.25rem'
        }}>
          Front Side Captured! ✅
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2, textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
              Great! Front side captured successfully
            </Typography>

            <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6, mb: 2 }}>
              Would you like to scan the back side for additional information, or continue with just the front?
            </Typography>

            {/* Preview of captured front */}
            {capturedImages.front && (
              <Box sx={{
                width: '100%',
                maxWidth: 200,
                aspectRatio: '1.586/1',
                borderRadius: '12px',
                overflow: 'hidden',
                margin: '0 auto',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                border: '2px solid #00ff88'
              }}>
                <img
                  src={capturedImages.front}
                  alt="Front side preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{
          p: 3,
          pt: 0,
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          '& > *': { flex: { xs: 1, sm: 'initial' } }
        }}>
          <Button
            onClick={handleContinueWithFrontOnly}
            variant="outlined"
            size="large"
            sx={{
              borderColor: '#00ff88',
              color: '#00ff88',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: '12px',
              '&:hover': {
                borderColor: '#00cc66',
                color: '#00cc66',
                background: 'rgba(0, 255, 136, 0.05)'
              }
            }}
          >
            Continue with Front Only
          </Button>

          <Button
            onClick={handleScanBackSide}
            variant="contained"
            size="large"
            sx={{
              background: 'linear-gradient(135deg, #ffaa00, #ff8800)',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(255, 170, 0, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #ff8800, #ff6600)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(255, 170, 0, 0.4)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            Scan Back Side
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSS Animation */}
      <style jsx global>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default Create;